import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

/** Critically damped Apple spring — no overshoot, ~0.4s settle */
export const springUi = { type: 'spring', bounce: 0, duration: 0.4 };
export const easeOut = [0.32, 0.72, 0, 1];

export const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: springUi,
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2, ease: easeOut },
  },
};

export const fadeUpVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { ...springUi, delay: i * 0.04 },
  }),
};

export const staggerContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.06 },
  },
};

export const staggerItemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springUi,
  },
};

export const tabFadeVariants = {
  initial: { opacity: 0, y: 6 },
  animate: {
    opacity: 1,
    y: 0,
    transition: springUi,
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.16, ease: easeOut },
  },
};

export const brandEntranceVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springUi,
  },
};

export function PageTransition({ children }) {
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className="page-transition">{children}</div>;
  }

  // Enter-only fade: AnimatePresence mode="wait" left a blank gap between routes
  // because the exiting page hid before the next page finished animating in.
  return (
    <motion.div
      key={location.pathname}
      className="page-transition"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springUi}
    >
      {children}
    </motion.div>
  );
}

export function FadeIn({ children, className, delay = 0, as = 'div' }) {
  const reduceMotion = useReducedMotion();
  const Component = motion[as] || motion.div;

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <Component
      className={className}
      custom={delay}
      variants={fadeUpVariants}
      initial="hidden"
      animate="visible"
    >
      {children}
    </Component>
  );
}

export function StaggerGrid({ children, className }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      variants={staggerContainerVariants}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div className={className} variants={staggerItemVariants}>
      {children}
    </motion.div>
  );
}

/** Soft Mota-style card hover lift */
export function SoftHover({ children, className, lift = 3, enabled = true }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion || !enabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      whileHover={{
        y: -lift,
        transition: springUi,
      }}
      style={{ willChange: 'transform' }}
    >
      {children}
    </motion.div>
  );
}

export function AnimatedPanel({ children, className, hover = true }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springUi}
      whileHover={
        hover
          ? {
              y: -2,
              transition: springUi,
            }
          : undefined
      }
    >
      {children}
    </motion.div>
  );
}

export function MotionSidebarItem({ children, className, isActive, delay = 0 }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.34, delay: delay * 0.85, ease: easeOut }}
      data-active={isActive || undefined}
    >
      {children}
    </motion.div>
  );
}

/** Viewport-aware fade-up reveal (Login / long panels) */
export function Reveal({ children, className, delay = 0, as = 'div', once = true }) {
  const reduceMotion = useReducedMotion();
  const Component = motion[as] || motion.div;

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <Component
      className={className}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.2 }}
      transition={{ duration: 0.45, delay, ease: easeOut }}
    >
      {children}
    </Component>
  );
}

/** Tab content morph — short opacity + y swap */
export function TabFade({ children, tabKey, className }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return (
      <div key={tabKey} className={className}>
        {children}
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={tabKey}
        className={className}
        variants={tabFadeVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/** Logo + wordmark entrance for Login */
export function BrandEntrance({ children, className }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      variants={brandEntranceVariants}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.div>
  );
}
